import { transact } from "@tldraw/state";
import { Doc, Error as ShareDBError } from "sharedb";

// Don't import from @teamwork/sharedb root, because the client is different
import ShareDB from "@teamwork/sharedb/lib/client";
import { keyBy } from "lodash";
import { useEffect, useRef, useState } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import { TLRecord, TLShape, createTLStore, defaultShapeUtils } from "tldraw";

import { ShareDbOp, ThrottledTLStore, throttleShareDbDoc, throttleTldrawStore } from "./helpers";

import { useAppDispatch } from "hooks/redux_hooks";
import { setResourceResetWhiteboard } from "shared/reducers/classroom";

const SHARE_DB_DOC_COLLECTION = "tldraw-whiteboards";
const SHARE_DB_THROTTLE_PERIOD_MS = 300;
const TLDRAW_THROTTLE_PERIOD_MS = 300;
const MAX_INITIALIZATION_ATTEMPTS = 3;

// Overrides TLStoreWithStatus type to use ThrottledTLStore
export declare type ThrottledTLStoreWithStatus =
  | {
      status: "error";
      store?: undefined;
      error: Error;
    }
  | {
      status: "loading";
      store?: undefined;
      error?: undefined;
    }
  | {
      status: "not-synced";
      store: ThrottledTLStore;
      error?: undefined;
    }
  | {
      status: "synced-local";
      store: ThrottledTLStore;
      error?: undefined;
    }
  | {
      status: "synced-remote";
      connectionStatus: "offline" | "online";
      store: ThrottledTLStore;
      error?: undefined;
    };

// Some context on ShareDB (via Peter W.):
// https://github.com/minervaproject/picasso/pull/11806/files#r1227477403
//
// We chose to use @teamwork/sharedb, which is an outdated/stale fork of sharedb because the fork had
// an implementation for "user presence", which we wanted to use in workbooks in 2019. Upstream Sharedb
// actually added their own presence implementation in March 2020, but we've been divergent since 2019.
// @teamwork/sharedb doesn't have typescript types, so we use the upstream types, which are slightly
// incompatible (@types/sharedb) but mostly helpful.
//
// Eventually, it might make sense to switch to sharedb since it's more actively maintainted.
// The OT types are still the same and standardized. Sadly, it'd require also updating SLE backend
// (and thus frontend), which would require us to, in the best case, slightly alter our user presence
// implementation, and worst case refactor a lot more.
//
// If we're trying to make presence work with the whiteboard eventually, I think it'd be worth making
// the switch to the official sharedb. While sharedb's DocPresence only works on richtext docs, Presence
// does work on json0. It's essentially just a messenger layer, but it might be more maintainable
// (and upgradable) compared to using our own pubsub/store for that.

// See https://github.com/tldraw/tldraw/blob/main/apps/examples/src/yjs/useYjsStore.ts
export function useShareDbStore({
  resourceId,
  hostUrl,
  isOwner,
  reset,
}: {
  resourceId: string;
  hostUrl: string;
  isOwner: boolean;
  reset: boolean;
}) {
  const dispatch = useAppDispatch();
  const [store] = useState(() =>
    createTLStore({
      shapeUtils: [...defaultShapeUtils],
    }),
  );
  const [throttledStoreWithStatus, setThrottledStoreWithStatus] =
    useState<ThrottledTLStoreWithStatus>({
      status: "loading",
    });
  const [shareDbDoc, setShareDbDoc] = useState<Doc | null>(null);
  const [socket, setSocket] = useState<ReconnectingWebSocket | null>(null);
  const [initializationAttempts, setInitializationAttempts] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const componentWillUnmount = useRef(false);

  useEffect(() => {
    if (!hostUrl || isInitialized) {
      return;
    }

    // Connect to ShareDB
    const _socket = new ReconnectingWebSocket(hostUrl, [], {
      // ShareDB handles dropped messages and buffering them while the socket is closed has undefined behavior
      maxEnqueuedMessages: 0,
    });
    setSocket(_socket);
    const connection = new ShareDB.Connection(_socket as any);
    const shareDbId = new URLSearchParams(hostUrl?.split("?")[1] || "").get("id") || resourceId;
    const doc = connection.get(SHARE_DB_DOC_COLLECTION, shareDbId);
    let timeout: NodeJS.Timeout | null = null;

    const cleanup = () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };

    doc.subscribe((error: ShareDBError) => {
      if (error) {
        window.flightLogger.error("[useShareDbStore] doc.subscribe error", error);
        return;
      }

      // If doc.type is undefined, the document has not been created yet
      // Question: where is doc.type defined?
      const whiteboardExistsForResource = doc.type;

      // If reset is true, the class was hard reset
      if (reset) {
        if (isOwner) {
          if (whiteboardExistsForResource) {
            window.flightLogger.log(
              `[useShareDbStore] Resetting whiteboard for resource ${resourceId}`,
            );
            doc.del((err) => {
              if (err) {
                window.flightLogger.error("[useShareDbStore] doc.del error", err);
              } else {
                doc.destroy();
                dispatch(setResourceResetWhiteboard({ resourceId, reset: false }));
              }
            });
          } else {
            window.flightLogger.log(
              `[useShareDbStore] There is no whiteboard to reset for resource ${resourceId}`,
            );
            dispatch(setResourceResetWhiteboard({ resourceId, reset: false }));
          }
        }
        return cleanup;
      }

      if (!whiteboardExistsForResource && isOwner) {
        // Set the doc with initial store records
        doc.create({ records: keyBy(store.allRecords(), "id") }, (createError: ShareDBError) => {
          if (createError) {
            window.flightLogger.error("[useShareDbStore] doc.create error", createError);
          } else {
            window.flightLogger.log("[useShareDbStore] doc.create success");
            setShareDbDoc(throttleShareDbDoc(doc, SHARE_DB_THROTTLE_PERIOD_MS));
            setThrottledStoreWithStatus({
              store: throttleTldrawStore(store, TLDRAW_THROTTLE_PERIOD_MS),
              status: "synced-remote",
              connectionStatus: "online",
            });
            setIsInitialized(true);
          }
        });
      } else {
        const initializeStore = () => {
          setInitializationAttempts((prev) => prev + 1);

          // Wait for the doc to get populated
          if (doc.type && doc.data.records["document:document"]) {
            window.flightLogger.log(
              "[useShareDbStore] Doc was already created, initializing store",
            );
            // Replace the store records with the ShareDB doc records
            transact(() => {
              store.clear();
              store.put(Object.values(doc.data.records));
              setShareDbDoc(throttleShareDbDoc(doc, SHARE_DB_THROTTLE_PERIOD_MS));
              setThrottledStoreWithStatus({
                store: throttleTldrawStore(store, TLDRAW_THROTTLE_PERIOD_MS),
                status: "synced-remote",
                connectionStatus: "online",
              });
            });
            setIsInitialized(true);
          } else {
            window.flightLogger.log(
              "[useShareDbStore] Waiting for doc to be created and populated",
            );
            timeout = setTimeout(initializeStore, 1000);
          }
        };
        initializeStore();
      }
    });

    return cleanup;
  }, [dispatch, reset, isOwner, hostUrl, resourceId, store, isInitialized]);

  useEffect(() => {
    return () => {
      componentWillUnmount.current = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (!componentWillUnmount.current) return;

      window.flightLogger.log(
        `[useShareDbStore] Unsubscribing from doc and closing socket for resource ${resourceId}`,
      );
      if (shareDbDoc) {
        shareDbDoc.unsubscribe();
      }
      if (socket) {
        socket.close();
      }
    };
  }, [resourceId, shareDbDoc, socket]);

  useEffect(() => {
    if (isOwner && initializationAttempts > MAX_INITIALIZATION_ATTEMPTS && !isInitialized) {
      window.flightLogger.log(
        "[useShareDbStore] initializeStore failed after three attempts, resetting whiteboard",
      );
      dispatch(setResourceResetWhiteboard({ resourceId, reset: true }));
    }
  }, [dispatch, initializationAttempts, isInitialized, isOwner, resourceId]);

  useEffect(() => {
    if (!shareDbDoc || !throttledStoreWithStatus.store) return;

    // Sync tldraw store changes to ShareDB doc
    window.flightLogger.log(
      `[useShareDbStore] Initializing store.listen for resource ${resourceId}`,
    );
    throttledStoreWithStatus.store.listen(
      ({ changes }) => {
        // TODO: prevent users from making edits to other users' records

        // Insert additions into ShareDB doc
        // changes.added is an object where the keys are the record ids and the values are the records
        const addOps: ShareDbOp[] = Object.values(changes.added).map((record) => ({
          p: ["records", record.id], // p = path
          oi: record, // oi = object insert
        }));

        // Insert updates into ShareDB doc
        // changes.updated is an object where the keys are the record ids and the values are [oldRecord, newRecord]
        const updateOps: ShareDbOp[] = Object.values(changes.updated)
          .filter(([_, record]) => {
            // To improve performance, do not send freehand shapes until they are complete
            const shape = record as TLShape;
            // @ts-ignore
            return !(shape.type === "draw" && !shape.props.isComplete);
          })
          .map(([_, record]) => ({
            p: ["records", record.id], // p = path
            oi: record, // oi = object insert
          }));

        // Remove deletions from ShareDB doc
        // changes.removed is an object where the keys are the record ids and the values are the records
        const removeOps: ShareDbOp[] = Object.values(changes.removed).map((record) => ({
          p: ["records", record.id], // p = path
          od: record, // od = object delete
        }));

        const allOps: ShareDbOp[] = addOps.concat(updateOps, removeOps);
        shareDbDoc.submitOp(allOps);
      },
      { source: "user", scope: "document" },
    );

    // Sync ShareDB doc changes to tldraw store
    const handleShareDbDocChanges = (ops: ShareDbOp[], source: boolean) => {
      // If source is true, then the change came from this user, so we don't need to apply it
      if (source) return;

      const toPut: TLRecord[] = [];
      const toRemove: TLRecord["id"][] = [];

      ops.forEach((op) => {
        const { oi, od } = op; // oi = object insert, od = object delete
        if (oi) {
          toPut.push(oi);
        } else if (od) {
          toRemove.push(od.id);
        }
      });

      throttledStoreWithStatus.store?.mergeChanges(toPut, toRemove);
    };
    window.flightLogger.log(
      `[useShareDbStore] Initializing shareDbDoc.on for resource ${resourceId}`,
    );
    shareDbDoc.on("op", handleShareDbDocChanges);

    return () => {
      window.flightLogger.log(`[useShareDbStore] Removing doc listener for resource ${resourceId}`);
      shareDbDoc.off("op", handleShareDbDocChanges);
    };
  }, [resourceId, shareDbDoc, store, throttledStoreWithStatus.store]);

  return throttledStoreWithStatus;
}
